'use client';
import React from 'react';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import { IntervalLine } from '../../../types';

interface IntervalDialogProps {
  open: boolean;
  onClose: () => void;
  intervalLines: IntervalLine[];
  onAddIntervalLines: (lines: IntervalLine[]) => void;
  onRemoveIntervalLine: (id: string) => void;
  xAxisData?: any[]; // Pass the X-axis data to determine type
  xAxisType?: 'date' | 'numeric' | 'category';
}

/**
 * IntervalDialog
 *
 * A modal dialog for generating and managing evenly spaced interval lines along a chart’s X-axis.
 * Supports time-based, numeric, or category-based X-axes, with flexible unit selection, line preview,
 * color customization, and deletion of existing interval lines.
 *
 * Designed for use in data visualization dashboards where users want to overlay periodic markers,
 * such as time intervals or distance bands.
 *
 * @component
 *
 * @param {Object} props - Props passed to the dialog component
 * @param {boolean} props.open - Controls whether the dialog is visible.
 * @param {() => void} props.onClose - Function called when the dialog is closed.
 * @param {IntervalLine[]} props.intervalLines - Array of currently active interval lines.
 * @param {(lines: IntervalLine[]) => void} props.onAddIntervalLines - Callback for adding newly generated interval lines.
 * @param {(id: string) => void} props.onRemoveIntervalLine - Callback for removing a single interval line by ID.
 * @param {any[]} [props.xAxisData] - Optional array of values from the X-axis (used to derive default starting point).
 * @param {'date' | 'numeric' | 'category'} [props.xAxisType='date'] - Type of the X-axis, determines units and formatting.
 *
 * @returns {JSX.Element} A dialog for configuring and previewing interval lines.
 *
 * @example
 * <IntervalDialog
 *   open={isOpen}
 *   onClose={() => setOpen(false)}
 *   intervalLines={intervals}
 *   onAddIntervalLines={(lines) => setIntervals([...intervals, ...lines])}
 *   onRemoveIntervalLine={(id) => setIntervals((prev) => prev.filter(l => l.id !== id))}
 *   xAxisData={timestamps}
 *   xAxisType="date"
 * />
 */

export const IntervalDialog = ({
  open,
  onClose,
  intervalLines,
  onAddIntervalLines,
  onRemoveIntervalLine,
  xAxisData = [],
  xAxisType = 'date',
}: IntervalDialogProps) => {
  const [startingPoint, setStartingPoint] = useState('');
  const [intervalValue, setIntervalValue] = useState('');
  const [intervalUnit, setIntervalUnit] = useState('minutes');
  const [numberOfLines, setNumberOfLines] = useState('5');
  const [label, setLabel] = useState('Interval');
  const [selectedColor, setSelectedColor] = useState('#ff4444');

  const colors = [
    { name: 'Red', value: '#ff0000' },
    { name: 'Green', value: '#00cc00' },
    { name: 'Blue', value: '#0066ff' },
    { name: 'Magenta', value: '#ff00ff' },
    { name: 'Cyan', value: '#00ffff' },
    { name: 'Orange', value: '#ff6600' },
    { name: 'Purple', value: '#9900cc' },
    { name: 'Lime', value: '#bfff00' },
    { name: 'Teal', value: '#008080' },
    { name: 'Pink', value: '#ff3399' },
    { name: 'Navy', value: '#000080' },
    { name: 'Strong Red', value: '#d50000' },
    { name: 'Strong Green', value: '#00b000' },
    { name: 'Strong Blue', value: '#0057e7' },
    { name: 'Dark Orange', value: '#ff6d00' },
    { name: 'Dark Magenta', value: '#aa00ff' },
    { name: 'Deep Cyan', value: '#0097a7' },
    { name: 'Dark Purple', value: '#6200ea' },
    { name: 'Deep Teal', value: '#004d40' },
    { name: 'Crimson', value: '#b71c1c' },
    { name: 'Indigo', value: '#1a237e' },
    { name: 'Forest Green', value: '#1b5e20' },
    { name: 'Midnight Blue', value: '#003366' },
  ];

  // Determine interval units based on X-axis type
  const getIntervalUnits = () => {
    if (xAxisType === 'date') {
      return [
        { label: 'Seconds', value: 'seconds' },
        { label: 'Minutes', value: 'minutes' },
        { label: 'Hours', value: 'hours' },
        { label: 'Days', value: 'days' },
      ];
    } else if (xAxisType === 'numeric') {
      return [
        { label: 'Units', value: 'units' },
        { label: 'Tens', value: 'tens' },
        { label: 'Hundreds', value: 'hundreds' },
      ];
    } else {
      return [{ label: 'Items', value: 'items' }];
    }
  };

  // Set default starting point based on X-axis data
  useEffect(() => {
    if (xAxisData && xAxisData.length > 0 && !startingPoint) {
      if (xAxisType === 'date') {
        // Use the first timestamp as default
        const firstDate = new Date(xAxisData[0]);
        setStartingPoint(firstDate.toISOString().slice(0, 16)); // Format for datetime-local input
      } else if (xAxisType === 'numeric') {
        setStartingPoint(String(xAxisData[0]));
      } else {
        setStartingPoint('0');
      }
    }
  }, [xAxisData, xAxisType, startingPoint]);

  const generateIntervalLines = () => {
    if (!startingPoint || !intervalValue || !numberOfLines) return [];

    const lines: IntervalLine[] = [];
    const numLines = Number.parseInt(numberOfLines);
    const interval = Number.parseFloat(intervalValue);

    for (let i = 0; i < numLines; i++) {
      let timestamp: Date;

      if (xAxisType === 'date') {
        const baseTime = new Date(startingPoint);
        let intervalMs = 0;

        switch (intervalUnit) {
          case 'seconds':
            intervalMs = interval * 1000;
            break;
          case 'minutes':
            intervalMs = interval * 60 * 1000;
            break;
          case 'hours':
            intervalMs = interval * 60 * 60 * 1000;
            break;
          case 'days':
            intervalMs = interval * 24 * 60 * 60 * 1000;
            break;
        }

        timestamp = new Date(baseTime.getTime() + i * intervalMs);
      } else if (xAxisType === 'numeric') {
        const baseValue = Number.parseFloat(startingPoint);
        let intervalStep = interval;

        switch (intervalUnit) {
          case 'tens':
            intervalStep = interval * 10;
            break;
          case 'hundreds':
            intervalStep = interval * 100;
            break;
        }

        // For numeric data, we'll create a fake date but store the numeric value
        timestamp = new Date(baseValue + i * intervalStep);
      } else {
        // Category type - just increment by interval
        const baseIndex = Number.parseInt(startingPoint) || 0;
        timestamp = new Date(baseIndex + i * interval);
      }

      lines.push({
        id: `interval-${Date.now()}-${i}`,
        startTime: timestamp,
        endTime: timestamp,
        label: `${label} ${i + 1}`,
        color: selectedColor,
        preset: `${intervalValue}${intervalUnit}`,
      });
    }

    return lines;
  };

  const handleAdd = () => {
    const newLines = generateIntervalLines();
    if (newLines.length > 0) {
      onAddIntervalLines(newLines);
      // Reset form
      setIntervalValue('');
      setNumberOfLines('5');
      setLabel('Interval');
    }
  };

  const getStartingPointInput = () => {
    if (xAxisType === 'date') {
      return (
        <Input
          id="startingPoint"
          type="datetime-local"
          value={startingPoint}
          onChange={(e) => setStartingPoint(e.target.value)}
        />
      );
    } else {
      return (
        <Input
          id="startingPoint"
          type="number"
          placeholder="Enter starting value"
          value={startingPoint}
          onChange={(e) => setStartingPoint(e.target.value)}
        />
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Interval Lines</DialogTitle>
          <p className="text-sm text-gray-600">
            X-axis type: <span className="font-semibold capitalize">{xAxisType}</span>
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="startingPoint">Starting Point {xAxisType === 'date' ? '(Date/Time)' : '(Value)'}</Label>
            {getStartingPointInput()}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="intervalValue">Interval</Label>
              <Input
                id="intervalValue"
                type="number"
                placeholder="Enter interval"
                value={intervalValue}
                onChange={(e) => setIntervalValue(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="intervalUnit">Unit</Label>
              <Select value={intervalUnit} onValueChange={setIntervalUnit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getIntervalUnits().map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="numberOfLines">Number of Lines</Label>
            <Input
              id="numberOfLines"
              type="number"
              min="1"
              max="50"
              value={numberOfLines}
              onChange={(e) => setNumberOfLines(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="label">Label Prefix</Label>
            <Input
              id="label"
              placeholder="Enter label prefix"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div>
            <Label>Color</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {colors.map((color) => (
                <button
                  key={color.value}
                  className={`w-8 h-8 rounded-full border-2 ${
                    selectedColor === color.value ? 'border-gray-800' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setSelectedColor(color.value)}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          <Button onClick={handleAdd} className="w-full" disabled={!startingPoint || !intervalValue || !numberOfLines}>
            <Plus className="w-4 h-4 mr-2" />
            Generate {numberOfLines} Lines
          </Button>

          {intervalLines.length > 0 && (
            <div>
              <Label>Current Intervals ({intervalLines.length})</Label>
              <div className="space-y-2 mt-2 max-h-32 overflow-y-auto">
                {intervalLines.map((line) => (
                  <div key={line.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: line.color }} />
                      <span className="text-sm">{line.label}</span>
                      {line.preset && <span className="text-xs text-gray-500">({line.preset})</span>}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-6 h-6"
                      onClick={() => onRemoveIntervalLine(line.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
